import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Input from '../Input';
import Button from '../Button';
import Dropdown from '../Dropdown';
import Toast from '../Toast';
import SubscriptionService, { RenewSubscriptionPayload } from '../../services/subscription.service';

interface RenewalModalProps {
  open: boolean;
  subscriptionId: number;
  currentAmount: string;
  currentCurrencyId: number;
  currentCurrencyCode: string;
  ownerEmail?: string;
  onClose: () => void;
  onRenewed: (newEndDate: string) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const PAYMENT_MODES: RenewSubscriptionPayload['payment_mode'][] = ['cash', 'bank_transfer', 'cheque', 'other'];

/**
 * US-040 AC-2: renewal reuses the payment-recording flow (F28's fields) but is
 * its own compact form rather than embedding PaymentForm — PaymentForm is a
 * full routed page, not a controlled component, so duplicating just the fields
 * needed here is simpler than restructuring it for modal use.
 */
const RenewalModal: React.FC<RenewalModalProps> = ({
  open,
  subscriptionId,
  currentAmount,
  currentCurrencyId,
  currentCurrencyCode,
  ownerEmail,
  onClose,
  onRenewed,
}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(currentAmount);
  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentMode, setPaymentMode] = useState<RenewSubscriptionPayload['payment_mode'] | ''>('');
  const [referenceNo, setReferenceNo] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setAmount(currentAmount);
      setPaymentDate(today());
      setPaymentMode('');
      setReferenceNo('');
      setNotes('');
      setError('');
    }
  }, [open, currentAmount]);

  const handleConfirm = useCallback(async () => {
    if (!paymentMode) {
      setError(t('subscriptions.renewalModal.pageTexts.fields.paymentMode.errors.required'));
      return;
    }
    setSubmitting(true);
    try {
      const response: any = await SubscriptionService.renew(subscriptionId, {
        amount: Number(amount),
        currency_id: currentCurrencyId,
        payment_date: paymentDate,
        payment_mode: paymentMode,
        reference_no: referenceNo || undefined,
        notes: notes || undefined,
      });
      const newEndDate = String(response?.data?.subscription?.end_date ?? '').slice(0, 10);
      const emailSent = response?.data?.ownerEmail ?? ownerEmail ?? '';
      toast.success(
        <Toast
          message={t('subscriptions.renewalModal.pageTexts.success', { endDate: newEndDate, email: emailSent })}
        />,
        { icon: false }
      );
      onRenewed(newEndDate);
    } finally {
      setSubmitting(false);
    }
  }, [amount, paymentDate, paymentMode, referenceNo, notes, subscriptionId, currentCurrencyId, ownerEmail, onRenewed, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('subscriptions.renewalModal.pageTexts.title')}
      content={
        <div className="row g-3">
          <div className="col-md-6">
            <Input
              id="renewal_amount"
              name="renewal_amount"
              type="number"
              min="0.01"
              label={t('subscriptions.renewalModal.pageTexts.fields.amount.label')}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Input
              id="renewal_currency"
              name="renewal_currency"
              label={t('subscriptions.renewalModal.pageTexts.fields.currency.label')}
              value={currentCurrencyCode}
              disabled
            />
          </div>
          <div className="col-md-6">
            <Input
              id="renewal_payment_date"
              name="renewal_payment_date"
              type="date"
              label={t('subscriptions.renewalModal.pageTexts.fields.paymentDate.label')}
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <Dropdown
              id="renewal_payment_mode"
              name="renewal_payment_mode"
              label={t('subscriptions.renewalModal.pageTexts.fields.paymentMode.label')}
              placeholder={t('payments.form.pageTexts.fields.paymentMode.placeholder')}
              value={paymentMode}
              error={error}
              options={PAYMENT_MODES.map((mode) => ({
                id: mode,
                name: t(`payments.form.pageTexts.fields.paymentMode.options.${mode}`),
              }))}
              noSearch
              onChange={(e) => setPaymentMode(e.target.value as RenewSubscriptionPayload['payment_mode'])}
            />
          </div>
          <div className="col-md-6">
            <Input
              id="renewal_reference_no"
              name="renewal_reference_no"
              label={t('payments.form.pageTexts.fields.referenceNo.label')}
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
            />
          </div>
          <div className="col-12">
            <Input
              id="renewal_notes"
              name="renewal_notes"
              label={t('payments.form.pageTexts.fields.notes.label')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
      }
      action={
        <>
          <Button className="btn btn-outline-secondary" text={t('subscriptions.form.pageTexts.buttons.cancel')} onClick={onClose} />
          <Button
            className="btn btn-primary"
            text={t('subscriptions.renewalModal.pageTexts.confirm')}
            loading={submitting}
            onClick={handleConfirm}
          />
        </>
      }
    />
  );
};

export default RenewalModal;
